<template>
	<div class="xs-12">
		<!-- Headline -->
		<div slot="headline" class="fs5 lh125 fw7 c1 mb2">{{ $t("actions") }}</div>

		<!-- Body -->
		<j-card class="bg5 bg4--l3">
			<!-- Request Payment -->
			<div class="flex xs-12 xs-middle xs-between p2 bb1 bc7">
				<div class="fs3 fw7 c6 lh125">{{ $t("request_payment") }}</div>
				<j-button
					size="xsmall"
					:isLoading="isRequestPaymentLoading"
					v-tooltip="requestPaymentTooltip"
					@click="requestPayment"
					action="requestPayment"
					>{{ $t("request") }}</j-button
				>
			</div>

			<!-- Issue -->
			<div class="flex xs-12 xs-middle xs-between p2 bb1 bc7">
				<div class="xs-8">
					<div class="xs-12 fs3 fw7 c6 lh125">{{ $t("issue_policy") }}</div>
				</div>
				<j-button
					:isLoading="isIssueLoading"
					v-tooltip="issueTooltip"
					size="xsmall"
					@click="issuePolicy"
					action="issuePolicy"
					>{{ $t("issue") }}</j-button
				>
			</div>

			<!-- Issue Manually -->
			<div class="flex xs-12 xs-middle xs-between p2 bb1 bc7">
				<div class="xs-8">
					<div class="xs-12 fs3 fw7 c6 lh125">
						{{ $t("issue_policy_manually") }}
					</div>
				</div>
				<j-button
					v-tooltip="issueManuallyTooltip"
					size="xsmall"
					@click="$modal.show('issueManually')"
				>
					{{ $t("issue_manually") }}
				</j-button>
			</div>

			<!-- Send Documents to Client -->
			<div class="flex xs-12 xs-middle xs-between p2">
				<div class="fs3 fw7 c6 lh125">
					{{ $t("send_issuance_confirmation") }}
				</div>
				<j-button
					size="xsmall"
					:isLoading="isSendConfirmationLoading"
					v-tooltip="sendConfirmationTooltip"
					@click="sendConfirmation"
					action="sendConfirmation"
					>{{ $t("send") }}</j-button
				>
			</div>
		</j-card>

		<!-- Modals -->
		<core-modal name="requestPaymentWarning" :headline="$t('request_payment')">
			<div class="p4">
				<h4 class="mb2">{{ $t("warning_heading") }}</h4>
				<ul class="ml4 lh150">
					<li
						class="xs-12 fs3 fw7 c6 lh125 mb2"
						v-for="(warning, i) in this.warnings"
						:key="i"
					>
						<span>{{ warning }}</span>
					</li>
				</ul>
				<h4 class="mt6">{{ $t("warning_request_payment_footer") }}</h4>
			</div>

			<div class="xs-12 xs-around p4 flex">
				<j-button
					class="tertiary"
					@click="$modal.hide('requestPaymentWarning')"
					>{{ $t("dismiss") }}</j-button
				>
				<j-button
					v-tooltip="
						'You may send a request for payment regardless of the above warnings'
					"
					@click="this.requestAnyway"
					:isLoading="isRequestPaymentLoading"
					action="continueRequestPayment"
					>{{ $t("continue") }}</j-button
				>
			</div>
		</core-modal>

		<core-modal name="issuePolicyWarning" :headline="$t('issue_policy')">
			<div class="p4">
				<h4 class="mb2">{{ $t("warning_heading") }}</h4>
				<ul class="ml4 lh150">
					<li
						class="xs-12 fs3 fw7 c6 lh125 mb2"
						v-for="(warning, i) in this.warnings"
						:key="i"
					>
						<span>{{ warning }}</span>
					</li>
				</ul>
				<h4 class="mt6">{{ $t("warning_issue_footer") }}</h4>
			</div>

			<div class="xs-12 xs-around p4 flex">
				<j-button class="tertiary" @click="$modal.hide('issuePolicyWarning')">{{
					$t("dismiss")
				}}</j-button>
				<j-button
					v-tooltip="'You may issue policy regardless of the above warnings'"
					@click="this.issueAnyway"
					:isLoading="isIssueLoading"
					>{{ $t("continue") }}</j-button
				>
			</div>
		</core-modal>

		<core-modal name="issueManually" :headline="$t('issue_policy_manually')">
			<div class="p4">
				<div class="lh125 mb2">
					{{ $t("issue_manually_description") }}
				</div>
				<core-form-input
					class="mt4"
					componentName="j-base-input-text"
					name="issuerNumber"
					label="Policy Number"
					v-model="issuerNumber"
				/>
			</div>

			<div class="xs-12 xs-between p4 flex">
				<j-button
					class="bordered"
					:isDisabled="isIssueManuallyLoading"
					@click="$modal.hide('issueManually')"
				>
					{{ $t("cancel") }}</j-button
				>
				<j-button
					@click="this.issuePolicyManually"
					:isLoading="isIssueManuallyLoading"
				>
					{{ $t("issue_policy") }}
				</j-button>
			</div>
		</core-modal>
	</div>
</template>

<script>
import CoreModal from "@/modules/core/components/CoreModal"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import JCard from "@/components/JCard"
import JButton from "@/components/JButton"
import get from "lodash.get"
import api from "../../api/"

// const api = () => {}

/**
 * This component is responsible for numerous actions that can happen on policy
 * It's a smart component that is calling APIs
 */
export default {
	components: { JCard, JButton, CoreModal, CoreFormInput },

	data() {
		return {
			isRequestPaymentLoading: false,
			isIssueLoading: false,
			isIssueManuallyLoading: false,
			isSendQuoteLoading: false,
			isSendConfirmationLoading: false,

			form: {
				to: null
			},

			forceIssue: false,
			warnings: null,

			// For manual issuance
			issuerNumber: null
		}
	},

	props: {
		policy: {
			type: Object,
			required: true
		}
	},

	computed: {
		requestPaymentTooltip() {
			return {
				placement: "auto",
				content: this.$t("request_payment_description"),
				delay: { show: 100 }
			}
		},

		issueTooltip() {
			return {
				placement: "auto",
				content: this.$t("issue_description"),
				delay: { show: 100 }
			}
		},

		issueManuallyTooltip() {
			return {
				placement: "auto",
				content: this.$t("issue_manually_description"),
				delay: { show: 100 }
			}
		},

		sendConfirmationTooltip() {
			return {
				placement: "auto",
				content: this.$t("send_documents_description"),
				delay: { show: 500 }
			}
		},
		policyId() {
			return this.$route.params.id
		}
	},

	methods: {
		async requestAnyway() {
			this.forceIssue = true
			await this.requestPayment()

			this.$modal.hide("requestPaymentWarning")

			this.forceIssue = false
			this.warnings = null
		},
		async issueAnyway() {
			this.forceIssue = true
			await this.issuePolicy()

			this.$modal.hide("issuePolicyWarning")

			this.forceIssue = false
			this.warnings = null
		},
		async requestPayment() {
			const { env, paymentProvider = "jetco" } = this.$store.state.settings

			// Start of loading
			this.isRequestPaymentLoading = true

			// 0: Validate policy
			// If invalid, just return. Errors are being shows in validatePolicy method
			const isPolicyValid = await this.validatePolicy()
			if (!isPolicyValid && !this.forceIssue) {
				this.isRequestPaymentLoading = false
				this.$modal.show("requestPaymentWarning")
				return
			}

			// prettier-ignore
			const redirectUrls = {
				jetco: `https://api.asia.covergo.com/j/${this.$store.state.tenantId}/covergo_crm/(transactionId)/p/0/en`,
				stripe: ""
			}

			const res = await api.initializeTransaction({
				input: {
					policyId: this.policyId,
					amount: this.policy?.premium?.amount,
					currencyCode: this.policy?.premium?.currencyCode,
					redirectToUrl: redirectUrls[paymentProvider]
				}
			})

			// 2: Send email
			// When successfull, send email notification with a link to our Jetco bridge app
			const url = get(res, "data.initializeTransaction.value.url")
			const token = get(res, "data.initializeTransaction.value.token")
			const transactionId = get(
				res,
				"data.initializeTransaction.value.transactionId"
			)

			// prettier-ignore
			const paymentLinks = {
				jetco: `https://payments.asia.covergo.com/bridge?url=${encodeURIComponent(url)}&token=${encodeURIComponent(token)}&provider=jetco`,
				stripe: `https://payments.asia.covergo.com/gateway?provider=stripe&transactionId=${transactionId}&amount=${this.amount}&tenantId=${this.$store.state.tenantId}&code=${encodeURIComponent(this.currencyCode)}`
			}

			const holderEmail = this.policy.holder.contacts?.filter(c => {
				return c.type === "email"
			})?.[0].value

			if (!holderEmail) {
				this.$store.dispatch("showError", this.$t("no_email"))
				return
			}

			const templateId = this.getPaymentTemplateId()

			const emailMessage = {
				from: env.VHIS_EMAIL_FROM,
				to: holderEmail,
				templateRendering: {
					templateId,
					input: {
						name: "data",
						contentJsonString: JSON.stringify({
							paymentLink: paymentLinks[paymentProvider],
							policy: { ...this.policy }
						})
					}
				}
			}

			// Send

			const sendEmailRes = await api.sendNotification({
				input: {
					emailMessage
				}
			})

			// Check errors
			if (sendEmailRes.errors) {
				this.$store.dispatch("showError", sendEmailRes.errors)
				this.isRequestPaymentLoading = false
				return
			}

			// Show success message
			this.$store.dispatch("showSuccess", this.$t("email_was_sent_to_client"))

			// End of loading
			this.isRequestPaymentLoading = false
		},
		async issuePolicy() {
			if (this.isIssueLoading === true) return // if it's already in progress

			this.isIssueLoading = true

			// 1: Validate policy
			// If invalid, just return. Errors are being shows in validatePolicy method
			const isPolicyValid = await this.validatePolicy()

			if (!isPolicyValid && !this.forceIssue) {
				this.isIssueLoading = false
				this.$modal.show("issuePolicyWarning")
				return
			}

			// 2: Issue
			const res = await api.issuePolicy({
				policyId: this.policyId,
				ignoreIssuanceValidation: true,
				ignoreCollateralValidation: true
			})
			this.isIssueLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// this can happen if there is a problem from insurer integration
			if (res.data.issuePolicy.status === "failure") {
				this.$store.dispatch("showError", "the policy failed to issue")
				return
			}

			// Success
			this.$emit("policyIssued")
			this.$modal.hide("issuePolicyWarning")
		},
		async sendConfirmation() {
			this.isSendConfirmationLoading = true

			const env = this.$store.state.settings.env

			const holderEmail = this.policy.holder.contacts
				?.filter(c => {
					return c.type === "email"
				})
				?.map(item => item.value)
				?.toString()

			if (!holderEmail) {
				this.$store.dispatch("showError", this.$t("no_email"))
				return
			}

			const templateId = this.getPolicyIssuanceTemplateId()

			const emailMessage = {
				from: env.VHIS_EMAIL_FROM,
				to: holderEmail,
				templateRendering: {
					templateId,
					input: {
						name: "data",
						variablesJsonString: JSON.stringify({
							policyWhere1: { id: this.policyId }
						})
					}
				}
			}

			const res = await api.sendNotification({
				input: {
					emailMessage
				}
			})

			this.isSendConfirmationLoading = false

			// Errros
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Success
			this.$store.dispatch("showSuccess", this.$t("email_was_sent"))
		},

		async issuePolicyManually() {
			this.isIssueManuallyLoading = true
			const res = await api.issuePolicy({
				policyId: this.policyId,
				issuerNumber: this.issuerNumber || null,
				ignoreIssuanceValidation: true,
				ignoreCollateralValidation: true,
				skipSendNotification: true,
				isManual: true
			})
			this.isIssueManuallyLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Success
			this.$emit("policyIssued")
			this.$modal.hide("issueManually")
		},

		/**
		 * This method does an API call to check if policy is valid and can be issued or payment can be requested
		 * If policy is valid, it returns true
		 * Else it returns false
		 */
		async validatePolicy() {
			const validated = await api.validatePolicy({
				policyId: this.policyId
			})
			// This query is rather special in terms of errors
			// errors are API errors whether errors_2 are errors to show to user
			if (
				validated.data.validatePolicy_2.errors ||
				validated.data.validatePolicy_2.errors_2
			) {
				if (validated.data.validatePolicy_2.errors)
					this.warnings = validated.data.validatePolicy_2.errors

				if (validated.data.validatePolicy_2.errors_2)
					this.warnings = validated.data.validatePolicy_2.errors_2

				return Promise.resolve(false)
			} else return Promise.resolve(true)
		},

		/**
		 * Method that will check if policy has accepted offer.
		 * If it doesn't have, it will show error and return false. Otherwise returns true.
		 * @returns true if it has otherwise false
		 */
		validateAcceptedOffer() {
			if (!this.acceptedOffer) {
				this.$store.dispatch("showError", this.$t("no_accepted_offer"))
				this.isIssueManuallyLoading = false
				return false
			}
			return true
		},

		getPaymentTemplateId() {
			const env = this.$store.state.settings.env
			const plan = this.policy?.product?.productId?.plan

			// Standard
			if (plan === "S00027-01-000-02")
				return env.EMAIL_VHIS_STANDARD_PAYMENT_CASE

			// Flexi Semi Private
			if (plan === "F00038-01-000-02")
				return env.EMAIL_VHIS_FLEXI_SEMIPRIVATE_PAYMENT_CASE

			// Flexi Private
			if (plan === "F00038-02-000-02")
				return env.EMAIL_VHIS_FLEXI_PRIVATE_PAYMENT_CASE

			// Asia iCare plan 1
			if (plan === "F00061-01-000-01")
				return env.EMAIL_ICARE_PLAN_1_PAYMENT_CASE

			// Asia iCare plan 1A
			if (plan === "F00061-01-001-01")
				return env.EMAIL_ICARE_PLAN_1A_PAYMENT_CASE

			// Asia iCare plan 2
			if (plan === "F00061-02-000-01")
				return env.EMAIL_ICARE_PLAN_2_PAYMENT_CASE

			// Asia iCare plan 2A
			if (plan === "F00061-02-001-01")
				return env.EMAIL_ICARE_PLAN_2A_PAYMENT_CASE

			// Asia iCare plan 2B
			if (plan === "F00061-02-002-01")
				return env.EMAIL_ICARE_PLAN_2B_PAYMENT_CASE
		},

		getPolicyIssuanceTemplateId() {
			const env = this.$store.state.settings.env

			const plan = this.policy.product.productId.plan

			const hasLoading = (this.policy.premium?.loadings || []).length

			const hasExclusion = (this.policy.exclusions || []).length

			const paymentFrequency = this.policy.values?.filter(item => {
				return item.key === "paymentFrequency"
			})?.[0].value?.stringValue

			// Standard
			if (plan === "S00027-01-000-02") {
				return hasLoading || hasExclusion
					? env.EMAIL_VHIS_STANDARD_POLICY_ISSUANCE_POLICY_HASENDORSEMENT
					: env.EMAIL_VHIS_STANDARD_POLICY_ISSUANCE_POLICY
			}
			// Flexi Semi Private - monthly
			if (plan === "F00038-01-000-02" && paymentFrequency === "monthly") {
				return hasLoading || hasExclusion
					? env.EMAIL_VHIS_FLEXI_SEMIPRIVATE_POLICY_ISSUANCE_MONTHLY_PAYMENT_POLICY_HASENDORSEMENT
					: env.EMAIL_VHIS_FLEXI_SEMIPRIVATE_POLICY_ISSUANCE_MONTHLY_PAYMENT_POLICY
			}

			// Flexi Semi Private - annually
			if (plan === "F00038-01-000-02" && paymentFrequency === "annually") {
				return hasLoading || hasExclusion
					? env.EMAIL_VHIS_FLEXI_SEMIPRIVATE_POLICY_ISSUANCE_ANNUAL_PAYMENT_POLICY_HASENDORSEMENT
					: env.EMAIL_VHIS_FLEXI_SEMIPRIVATE_POLICY_ISSUANCE_ANNUAL_PAYMENT_POLICY
			}

			// Flexi Private - monthly
			if (plan === "F00038-02-000-02" && paymentFrequency === "monthly") {
				return hasLoading || hasExclusion
					? env.EMAIL_VHIS_FLEXI_PRIVATE_POLICY_ISSUANCE_MONTHLY_PAYMENT_POLICY_HASENDORSEMENT
					: env.EMAIL_VHIS_FLEXI_PRIVATE_POLICY_ISSUANCE_MONTHLY_PAYMENT_POLICY
			}

			// Flexi Private - annually
			if (plan === "F00038-02-000-02" && paymentFrequency === "annually") {
				return hasLoading || hasExclusion
					? env.EMAIL_VHIS_FLEXI_PRIVATE_POLICY_ISSUANCE_ANNUAL_PAYMENT_POLICY_HASENDORSEMENT
					: env.EMAIL_VHIS_FLEXI_PRIVATE_POLICY_ISSUANCE_ANNUAL_PAYMENT_POLICY
			}

			// Asia iCare plan 1 - monthly
			if (plan === "F00061-01-000-01" && paymentFrequency === "monthly") {
				return hasLoading || hasExclusion
					? env.EMAIL_ICARE_PLAN_1_POLICY_ISSUANCE_MONTHLY_PAYMENT_POLICY_HASENDORSEMENT
					: env.EMAIL_ICARE_PLAN_1_POLICY_ISSUANCE_MONTHLY_PAYMENT_POLICY
			}

			// Asia iCare plan 1 - annually
			if (plan === "F00061-01-000-01" && paymentFrequency === "annually") {
				return hasLoading || hasExclusion
					? env.EMAIL_ICARE_PLAN_1_POLICY_ISSUANCE_ANNUAL_PAYMENT_POLICY_HASENDORSEMENT
					: env.EMAIL_ICARE_PLAN_1_POLICY_ISSUANCE_ANNUAL_PAYMENT_POLICY
			}

			// Asia iCare plan 1A - monthly
			if (plan === "F00061-01-001-01" && paymentFrequency === "monthly") {
				return hasLoading || hasExclusion
					? env.EMAIL_ICARE_PLAN_1A_POLICY_ISSUANCE_MONTHLY_PAYMENT_POLICY_HASENDORSEMENT
					: env.EMAIL_ICARE_PLAN_1A_POLICY_ISSUANCE_MONTHLY_PAYMENT_POLICY
			}

			// Asia iCare plan 1A - annually
			if (plan === "F00061-01-001-01" && paymentFrequency === "annually") {
				return hasLoading || hasExclusion
					? env.EMAIL_ICARE_PLAN_1A_POLICY_ISSUANCE_ANNUAL_PAYMENT_POLICY_HASENDORSEMENT
					: env.EMAIL_ICARE_PLAN_1A_POLICY_ISSUANCE_ANNUAL_PAYMENT_POLICY
			}

			// Asia iCare plan 2 - monthly
			if (plan === "F00061-02-000-01" && paymentFrequency === "monthly") {
				return hasLoading || hasExclusion
					? env.EMAIL_ICARE_PLAN_2_POLICY_ISSUANCE_MONTHLY_PAYMENT_POLICY_HASENDORSEMENT
					: env.EMAIL_ICARE_PLAN_2_POLICY_ISSUANCE_MONTHLY_PAYMENT_POLICY
			}

			// Asia iCare plan 2 - annually
			if (plan === "F00061-02-000-01" && paymentFrequency === "annually") {
				return hasLoading || hasExclusion
					? env.EMAIL_ICARE_PLAN_2_POLICY_ISSUANCE_ANNUAL_PAYMENT_POLICY_HASENDORSEMENT
					: env.EMAIL_ICARE_PLAN_2_POLICY_ISSUANCE_ANNUAL_PAYMENT_POLICY
			}

			// Asia iCare plan 2A - monthly
			if (plan === "F00061-02-001-01" && paymentFrequency === "monthly") {
				return hasLoading || hasExclusion
					? env.EMAIL_ICARE_PLAN_2A_POLICY_ISSUANCE_MONTHLY_PAYMENT_POLICY_HASENDORSEMENT
					: env.EMAIL_ICARE_PLAN_2A_POLICY_ISSUANCE_MONTHLY_PAYMENT_POLICY
			}

			// Asia iCare plan 2A - annually
			if (plan === "F00061-02-001-01" && paymentFrequency === "annually") {
				return hasLoading || hasExclusion
					? env.EMAIL_ICARE_PLAN_2A_POLICY_ISSUANCE_ANNUAL_PAYMENT_POLICY_HASENDORSEMENT
					: env.EMAIL_ICARE_PLAN_2A_POLICY_ISSUANCE_ANNUAL_PAYMENT_POLICY
			}

			// Asia iCare plan 2B - monthly
			if (plan === "F00061-02-002-01" && paymentFrequency === "monthly") {
				return hasLoading || hasExclusion
					? env.EMAIL_ICARE_PLAN_2B_POLICY_ISSUANCE_MONTHLY_PAYMENT_POLICY_HASENDORSEMENT
					: env.EMAIL_ICARE_PLAN_2B_POLICY_ISSUANCE_MONTHLY_PAYMENT_POLICY
			}

			// Asia iCare plan 2B - annually
			if (plan === "F00061-02-002-01" && paymentFrequency === "annually") {
				return hasLoading || hasExclusion
					? env.EMAIL_ICARE_PLAN_2B_POLICY_ISSUANCE_ANNUAL_PAYMENT_POLICY_HASENDORSEMENT
					: env.EMAIL_ICARE_PLAN_2B_POLICY_ISSUANCE_ANNUAL_PAYMENT_POLICY
			}
		}
	}
}
</script>
